import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Keyboard, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ASK_SILAS_TRIGGER_SELECTOR,
  isEditableShortcutTarget,
} from '@/lib/keyboardShortcuts';

const ASK_SILAS_FOCUS_KEY = 'ask-silas-focus-after-navigation';

const ROUTE_SHORTCUTS: Record<string, string> = {
  '1': '/command',
  '2': '/intelligence',
  '3': '/finance',
  '4': '/kpis',
};

export function OperatorShortcuts() {
  const [open, setOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (window.sessionStorage.getItem(ASK_SILAS_FOCUS_KEY) !== 'true') return;
    window.sessionStorage.removeItem(ASK_SILAS_FOCUS_KEY);
    window.setTimeout(() => {
      const trigger = document.querySelector<HTMLButtonElement>(ASK_SILAS_TRIGGER_SELECTOR);
      trigger?.focus();
      if (trigger) setAnnouncement('Ask Silas focused.');
    }, 80);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const focusAskSilas = () => {
      const trigger = document.querySelector<HTMLButtonElement>(ASK_SILAS_TRIGGER_SELECTOR);
      if (trigger) {
        trigger.focus();
        setAnnouncement('Ask Silas focused.');
        return;
      }

      window.sessionStorage.setItem(ASK_SILAS_FOCUS_KEY, 'true');
      navigate('/command');
      setAnnouncement('Opening Command so Ask Silas can be focused.');
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableShortcutTarget(event.target)) return;

      if (event.key === 'Escape') {
        setOpen(false);
        return;
      }

      if (event.key === '?' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        setOpen((current) => !current);
        return;
      }

      if (!event.altKey || event.ctrlKey || event.metaKey) return;
      const key = event.key.toLowerCase();

      if (key === 'a') {
        event.preventDefault();
        focusAskSilas();
        return;
      }

      if (key === 'r') {
        event.preventDefault();
        void queryClient.invalidateQueries();
        setAnnouncement('Current dashboard data is refreshing.');
        return;
      }

      const route = ROUTE_SHORTCUTS[key];
      if (route) {
        event.preventDefault();
        navigate(route);
        setAnnouncement(`Navigated to ${route.replace('/', '') || 'dashboard'}.`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, queryClient]);

  return (
    <>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="operator-shortcuts-title"
            className="w-full max-w-[560px] overflow-hidden rounded-lg border border-[var(--border-color)] bg-surface-raised shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="flex items-center justify-between border-b border-[var(--border-color)] px-5 py-4">
              <div className="flex items-center gap-2">
                <Keyboard className="h-5 w-5 text-[var(--accent)]" />
                <h2
                  id="operator-shortcuts-title"
                  className="text-lg font-semibold text-[var(--text-primary)]"
                >
                  Operator shortcuts
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-1.5 text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                aria-label="Close shortcut help"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="max-h-[62vh] overflow-y-auto p-5">
              <ShortcutSection title="Global">
                <ShortcutRow keys={['Alt', '+', 'A']} desc="Focus Ask Silas" />
                <ShortcutRow keys={['Alt', '+', 'R']} desc="Refresh this view" />
                <ShortcutRow keys={['Alt', '+', '1']} desc="Go to Command" />
                <ShortcutRow keys={['Alt', '+', '2']} desc="Go to Intelligence" />
                <ShortcutRow keys={['Alt', '+', '3']} desc="Go to Finance" />
                <ShortcutRow keys={['Alt', '+', '4']} desc="Go to KPIs" />
                <ShortcutRow keys={['Ctrl/Cmd', '+', 'K']} desc="Open command palette" />
                <ShortcutRow keys={['?']} desc="Show this help" />
              </ShortcutSection>

              <ShortcutSection title="Command Grid">
                <ShortcutRow keys={['Arrow Keys']} desc="Move between rate cells" />
                <ShortcutRow keys={['Shift', '+', 'Arrow']} desc="Extend selection" />
                <ShortcutRow keys={['Space']} desc="Add focused cell to selection" />
                <ShortcutRow keys={['Enter']} desc="Arm or approve selected rate" />
                <ShortcutRow keys={['Delete']} desc="Skip selected rate" />
                <ShortcutRow keys={['Ctrl/Cmd', '+', 'Z']} desc="Undo last action" />
              </ShortcutSection>

              <div className="mt-5 rounded-lg border border-[var(--border-color)] bg-[var(--surface)] px-3 py-2 text-xs text-[var(--text-tertiary)]">
                Shortcuts pause while you are typing in an input, textarea, select, or editable
                note.
              </div>
            </div>

            <footer className="border-t border-[var(--border-color)] bg-[var(--surface)]/50 px-5 py-3 text-center text-xs text-[var(--text-muted)]">
              Press <ShortcutKey label="Esc" /> or <ShortcutKey label="?" /> to close
            </footer>
          </section>
        </div>
      ) : null}
    </>
  );
}

function ShortcutSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-5 last:mb-0">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function ShortcutRow({ keys, desc }: { keys: string[]; desc: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="min-w-0 text-sm text-[var(--text-secondary)]">{desc}</span>
      <div className="flex shrink-0 items-center gap-1">
        {keys.map((key, index) => (
          <Fragment key={`${key}-${index}`}>
            {key === '+' ? (
              <span className="text-xs text-[var(--text-muted)]">+</span>
            ) : (
              <ShortcutKey label={key} />
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function ShortcutKey({ label }: { label: string }) {
  return (
    <kbd className="min-w-6 rounded bg-[var(--surface-hover)] px-2 py-1 text-center font-mono text-xs text-[var(--text-secondary)]">
      {label}
    </kbd>
  );
}
